import { useEffect, useRef, useState } from 'react'
import {
  SearchApi,
  FilterCollapsableComponent,
  CategoryPossibleValues,
  HandleToggleClickParams,
  UseReduxSearchProviderResponse,
  AllowedFacets,
  FacetsOrderAndIsOpen,
  SortObject,
} from '../types'
import { ConstructorFacetService } from 'components/SA/SearchProvider/helpers/ConstructorFacetService'
import { buildSelectedFacetsObject } from 'components/SA/SearchProvider/helpers/facets'
import { getHrefFunction, updateClientSideUrl } from 'components/SA/SearchProvider/helpers/url'
import { transformFacetsToFilters } from '../helpers/transformFacetsToFilters'
import useReduxSearchProvider from './useReduxSearchProvider'

type useGetFacetsParams = {
  nextAppRoute: string
  baseUrl: string
  searchService: SearchApi
  searchQuery: string
  selectedFilters: Record<string, ReadonlyArray<string>>
  predefinedFilters?: Record<string, ReadonlyArray<string>>
  page: number
  resultsPerPage: number
  destinationZone: string
  selectedCategory: CategoryPossibleValues
  isPrintsPage?: boolean
  userSizePreference: string
  useReduxProviderHook?: () => UseReduxSearchProviderResponse
  hardcodedFacets?: AllowedFacets
  hardcodedFacetsOrderAndIsOpen?: FacetsOrderAndIsOpen
  shouldUpdateFacetsReduxStore?: boolean
  defaultSortObject?: SortObject
}

type useGetFacetsReturn = {
  facetsList: FilterCollapsableComponent
  isLoadingFacets: boolean
}

const useGetFacets = ({
  nextAppRoute,
  baseUrl,
  searchService,
  searchQuery,
  selectedFilters,
  predefinedFilters,
  page,
  resultsPerPage,
  destinationZone,
  selectedCategory,
  isPrintsPage,
  userSizePreference,
  useReduxProviderHook = useReduxSearchProvider,
  hardcodedFacets = {},
  hardcodedFacetsOrderAndIsOpen = {},
  shouldUpdateFacetsReduxStore = false,
  defaultSortObject,
}: useGetFacetsParams): useGetFacetsReturn => {
  const isFirstRender = useRef<boolean>(true)
  const [isLoadingFacets, setIsLoadingFacets] = useState<boolean>(false)
  const [facetsList, setFacetsList] = useState<FilterCollapsableComponent>([])
  // getting facets from redux
  const {
    facetsFromSearchProvider,
    selectedFacets,
    sort,
    basicQueryParams,
    pathParams,
    sharedFacetNames,
    updateReduxStore,
  } = useReduxProviderHook()

  const handleToggleClick = ({ isInPathParam, facetName }: HandleToggleClickParams) => {
    const newSelectedFacets = buildSelectedFacetsObject({
      selectedFacets,
      facetName,
      facetValue: 'true',
      inputType: 'single',
    })
    const href = getHrefFunction({
      facetsAndQueryParams: {
        ...newSelectedFacets,
        ...basicQueryParams,
        page: [''],
      },
      pathParams,
      sharedFacetNames,
    })()
    const newUrlPath = `${baseUrl}${href}`

    updateClientSideUrl(newUrlPath, nextAppRoute)
  }

  const getFacetsFromProvider = async (facetsFromRedux) => {
    setIsLoadingFacets(true)
    let facetsToTransform = facetsFromRedux

    // Request facets from provider on client side only in the first render. For client side transition should just transform the data from redux to filters
    if (Boolean(isFirstRender.current)) {
      const hasSearchQuery = Boolean(searchQuery)
      const isSpecificCategorySelected = Boolean(selectedCategory) && selectedCategory !== 'all'
      const shouldAddGroupIdFilter = hasSearchQuery || isSpecificCategorySelected

      const { success, payload } = await searchService.fetchSearchProviderFacets({
        query: searchQuery,
        filters: selectedFilters,
        predefinedFilters,
        sort,
        page,
        resultsPerPage,
        destinationZone,
        selectedCategory,
        isPrintsPage,
        userSizePreference,
        shouldAddGroupIdFilter,
        defaultSortObject,
      })

      if (success) {
        facetsToTransform = payload.data
        if (Boolean(shouldUpdateFacetsReduxStore)) {
          updateReduxStore({ facets: payload.data })
        }
      }
    }

    const facetService = new ConstructorFacetService()
    const transformedFacetsList = transformFacetsToFilters({
      facets: facetsToTransform,
      baseUrl,
      nextAppRoute,
      facetService,
      selectedCategory,
      handleToggleClick,
      selectedFacets,
      hardcodedFacets,
      hardcodedFacetsOrderAndIsOpen,
    })
    setFacetsList(transformedFacetsList)
    setIsLoadingFacets(false)
    isFirstRender.current = false
  }

  useEffect(() => {
    getFacetsFromProvider(facetsFromSearchProvider)
  }, [facetsFromSearchProvider, selectedFacets])

  return {
    facetsList,
    isLoadingFacets,
  }
}

export default useGetFacets
